import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Maximize2, ArrowUpRight, PlayCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { APPS, waLink } from "@/data/content";
import { Reveal, MaskLine, Eyebrow, EASE } from "./Reveal";

export const selectSuiteApp = (id) => window.dispatchEvent(new CustomEvent("suite:select", { detail: id }));

const Dock = ({ active, onPick }) => (
  <div data-testid="suite-dock" className="flex lg:grid lg:grid-cols-9 gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 snap-x">
    {APPS.map((a) => (
      <button
        key={a.id}
        data-testid={`suite-dock-${a.id}`}
        onClick={() => onPick(a.id)}
        className={`group relative snap-start shrink-0 w-[104px] lg:w-auto flex flex-col items-center gap-2 rounded-sm border px-2 py-4 transition-[border-color,background-color] duration-300 ${
          active === a.id ? "border-[var(--cyan)]/60 bg-[var(--blue)]/10" : "border-white/10 bg-[var(--navy)]/60 hover:border-white/30"
        }`}
      >
        <img src={a.logo} alt="" className={`h-14 w-14 object-contain transition-transform duration-500 ${active === a.id ? "scale-110" : "group-hover:scale-105"}`} />
        <span className={`text-xs font-medium text-center leading-tight ${active === a.id ? "text-white" : "text-[var(--steel-2)]"}`}>{a.name}</span>
        {active === a.id && <motion.span layoutId="dock-bar" className="absolute -bottom-px left-3 right-3 h-[2px] bg-[var(--cyan)]" />}
      </button>
    ))}
  </div>
);

const Screen = ({ app, shot, setShot, onOpen }) => (
  <div className="rounded-md border border-white/15 bg-[#0B132B] overflow-hidden shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]">
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10">
      <img src={app.logo} alt="" className="h-5 w-5 object-contain" />
      <span className="text-sm text-white truncate">{app.full}</span>
      <span className="ml-auto font-mono text-[11px] text-[var(--steel-2)] hidden sm:block">{app.shots[shot].label}</span>
    </div>
    <button data-testid="suite-screen-open" onClick={onOpen} className="group relative block w-full aspect-[1444/745] bg-white overflow-hidden">
      <motion.img
        key={app.shots[shot].src}
        src={app.shots[shot].src}
        alt={`${app.full} — ${app.shots[shot].label}`}
        initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
        animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="absolute inset-0 w-full h-full object-contain"
      />
      <span className="absolute right-3 bottom-3 grid h-9 w-9 place-items-center rounded-full bg-[#0B132B]/85 text-white opacity-80 group-hover:opacity-100 transition-opacity"><Maximize2 size={16} /></span>
    </button>
    {app.shots.length > 1 && (
      <div className="flex gap-2 overflow-x-auto p-3 border-t border-white/10">
        {app.shots.map((s, i) => (
          <button key={s.src} data-testid={`suite-shot-${i}`} onClick={() => setShot(i)} title={s.label}
            className={`shrink-0 w-24 sm:w-28 aspect-[16/9] rounded-sm overflow-hidden border bg-white transition-opacity ${i === shot ? "border-[var(--cyan)]" : "border-white/10 opacity-55 hover:opacity-100"}`}>
            <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const Details = ({ app }) => (
  <motion.div key={app.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} data-testid="suite-details">
    <div className="flex items-center gap-4">
      <img src={app.logo} alt="" className="h-16 w-16 object-contain" />
      <div>
        <h3 data-testid="suite-app-name" className="font-display text-4xl md:text-5xl font-black uppercase text-white leading-none">{app.name}</h3>
        <p className="mt-1 text-sm text-[var(--steel-2)]">{app.tagline}</p>
      </div>
    </div>
    <div className="mt-5 flex gap-2 font-mono text-[11px] uppercase tracking-widest">
      <span className="rounded-full border border-white/15 px-3 py-1 text-[var(--steel)]">Windows</span>
      <span className="rounded-full border border-emerald-400/40 px-3 py-1 text-emerald-300">Captura real</span>
    </div>
    <p className="mt-5 text-[var(--steel-2)] leading-relaxed">{app.text}</p>
    <p className="mt-4 text-sm text-[var(--steel)]"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--amber)] mr-2">Para</span>{app.para}</p>
    <ul className="mt-6 space-y-2.5">
      {app.features.map((f) => (
        <li key={f} className="flex gap-3 text-sm text-[var(--steel)]"><Check size={16} className="mt-0.5 shrink-0 text-[var(--cyan)]" />{f}</li>
      ))}
    </ul>
    <div className="mt-8 flex flex-wrap gap-3">
      <a data-testid="suite-demo-btn" href={waLink(`Olá! Gostaria de solicitar uma demonstração do ${app.full}.`)} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-[var(--blue)] px-6 py-3 text-sm font-medium text-white hover:bg-[#4a7dff] transition-colors">
        <PlayCircle size={16} /> Solicitar demonstração
      </a>
      <a data-testid="suite-buy-btn" href={app.url || waLink(`Olá! Quero adquirir uma licença do ${app.full}.`)} target="_blank" rel="noreferrer"
        className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white hover:border-[var(--cyan)] hover:text-[var(--cyan)] transition-colors">
        Adquirir licença <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
      </a>
    </div>
  </motion.div>
);

const Lightbox = ({ app, shot, setShot, open, setOpen }) => {
  const n = app.shots.length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="suite-lightbox" className="max-w-[min(1280px,96vw)] border-white/15 bg-[#0B132B] p-3 sm:p-4">
        <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--cyan)] pr-8">{app.full} · {app.shots[shot].label}</DialogTitle>
        <div className="relative">
          <img src={app.shots[shot].src} alt={app.shots[shot].label} className="w-full max-h-[80vh] object-contain bg-white rounded-sm" />
          {n > 1 && (
            <>
              <button data-testid="suite-lightbox-prev" aria-label="Anterior" onClick={() => setShot((shot - 1 + n) % n)} className="absolute left-2 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-[#0B132B]/85 text-white"><ChevronLeft /></button>
              <button data-testid="suite-lightbox-next" aria-label="Próxima" onClick={() => setShot((shot + 1) % n)} className="absolute right-2 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-[#0B132B]/85 text-white"><ChevronRight /></button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Suite = () => {
  const [id, setId] = useState(APPS[0].id);
  const [shot, setShot] = useState(0);
  const [open, setOpen] = useState(false);
  const app = APPS.find((a) => a.id === id) ?? APPS[0];

  const pick = (next) => { setId(next); setShot(0); };

  useEffect(() => {
    const onSelect = (e) => pick(e.detail);
    window.addEventListener("suite:select", onSelect);
    return () => window.removeEventListener("suite:select", onSelect);
  }, []);

  return (
    <section id="aplicativos" data-testid="apps-section" className="relative bg-[var(--navy-2)] py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 blueprint opacity-40" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <Reveal><Eyebrow testid="apps-eyebrow">FMACIEL Suite · 9 aplicativos para Windows</Eyebrow></Reveal>
            <h2 className="mt-6 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">
              <MaskLine inView>Ferramentas feitas</MaskLine>
              <MaskLine inView delay={0.1}><span className="steel-text">por quem projeta.</span></MaskLine>
            </h2>
          </div>
          <Reveal delay={0.2} className="max-w-md">
            <p className="text-base md:text-lg text-[var(--steel-2)] leading-relaxed">
              Todos no mesmo padrão: dados organizados, verificações automáticas e relatório em PDF pronto para
              entregar. Escolha um aplicativo para ver a tela.
            </p>
          </Reveal>
        </div>

        <Reveal y={20}><Dock active={id} onPick={pick} /></Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-8 min-w-0" y={30}>
            <Screen app={app} shot={shot} setShot={setShot} onOpen={() => setOpen(true)} />
          </Reveal>
          <div className="lg:col-span-4 min-w-0"><Details app={app} /></div>
        </div>

        <p className="mt-10 flex items-center gap-3 text-sm text-[var(--steel-2)]">
          <Check size={16} className="text-emerald-300" /> Capturas reais das versões atuais. Dados de clientes foram ocultados. Clique na tela para ampliar.
        </p>
      </div>
      <Lightbox app={app} shot={shot} setShot={setShot} open={open} setOpen={setOpen} />
    </section>
  );
};
